import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Progress } from '../ui/progress';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';
import { 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  Download, 
  Filter,
  Users,
  Award,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import { CredentialUtilizationMetrics, AnalyticsTimeRange, ExportOptions } from '../../types/AnalyticsTypes';

interface CredentialUtilizationReportProps {
  data: CredentialUtilizationMetrics;
  dateRange: AnalyticsTimeRange;
  onExport: (options: ExportOptions) => void;
}

const COLORS = ['#3B82F6', '#10B981', '#FBBF24', '#EF4444', '#8B5CF6', '#F59E0B'];

export default function CredentialUtilizationReport({ data, dateRange, onExport }: CredentialUtilizationReportProps) {
  const [selectedView, setSelectedView] = useState<'overview' | 'issuers' | 'verifiers' | 'types'>('overview');

  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="h-4 w-4 text-verified-green" />;
      case 'down':
        return <TrendingDown className="h-4 w-4 text-red-500" />;
      default:
        return <Minus className="h-4 w-4 text-warm-slate" />;
    }
  };

  const getTrendColor = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up':
        return 'text-verified-green';
      case 'down':
        return 'text-red-500';
      default:
        return 'text-warm-slate';
    }
  };

  const handleExport = () => {
    onExport({
      format: 'pdf',
      includeCharts: true,
      includeRawData: true,
      dateRange,
      reportTypes: ['credentials']
    });
  };

  // Prepare chart data
  const utilizationData = data.credentialsByType.map(item => ({
    name: item.type,
    issued: item.issued,
    verified: item.verified,
    requested: item.requested
  }));

  const industryData = data.utilizationByIndustry.map(item => ({
    name: item.industry,
    usage: item.usage,
    growth: item.growth
  }));

  const pieData = data.credentialsByType.map((item, index) => ({
    name: item.type,
    value: item.issued,
    color: COLORS[index % COLORS.length]
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-display text-charcoal-ink">Credential Utilization Report</h2>
          <p className="text-warm-slate">
            Comprehensive analysis of credential issuance, verification, and usage patterns
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
          <Button onClick={handleExport} size="sm" className="credential-capital-button-primary">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="credential-capital-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-warm-slate">Total Credentials</p>
                <p className="text-2xl font-semibold text-charcoal-ink">
                  {data.totalCredentials.toLocaleString()}
                </p>
                <div className="flex items-center mt-2">
                  <Badge variant="outline" className="text-xs">
                    Active: {data.activeCredentials.toLocaleString()}
                  </Badge>
                </div>
              </div>
              <div className="p-3 rounded-full bg-electric-steel/10">
                <Award className="h-6 w-6 text-electric-steel" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="credential-capital-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-warm-slate">Daily Issuance Rate</p>
                <p className="text-2xl font-semibold text-charcoal-ink">
                  {data.issuanceRate.daily}
                </p>
                <div className="flex items-center mt-2">
                  <Badge variant="outline" className="text-xs">
                    Monthly: {data.issuanceRate.monthly}
                  </Badge>
                </div>
              </div>
              <div className="p-3 rounded-full bg-verified-green/10">
                <CheckCircle className="h-6 w-6 text-verified-green" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="credential-capital-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-warm-slate">Daily Verification Rate</p>
                <p className="text-2xl font-semibold text-charcoal-ink">
                  {data.verificationRate.daily}
                </p>
                <div className="flex items-center mt-2">
                  <Badge variant="outline" className="text-xs">
                    Monthly: {data.verificationRate.monthly}
                  </Badge>
                </div>
              </div>
              <div className="p-3 rounded-full bg-electric-steel/10">
                <Users className="h-6 w-6 text-electric-steel" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="credential-capital-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-warm-slate">Expired Credentials</p>
                <p className="text-2xl font-semibold text-charcoal-ink">
                  {data.expiredCredentials.toLocaleString()}
                </p>
                <div className="flex items-center mt-2">
                  <Badge variant="outline" className="text-xs text-red-600">
                    Revoked: {data.revokedCredentials}
                  </Badge>
                </div>
              </div>
              <div className="p-3 rounded-full bg-red-500/10">
                <XCircle className="h-6 w-6 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* View Selector */}
      <Card className="credential-capital-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-display text-charcoal-ink">Detailed Analysis</CardTitle>
              <CardDescription>
                Choose a view to explore credential utilization patterns
              </CardDescription>
            </div>
            <div className="flex space-x-2">
              {[
                { key: 'overview', label: 'Overview' },
                { key: 'issuers', label: 'Top Issuers' },
                { key: 'verifiers', label: 'Top Verifiers' },
                { key: 'types', label: 'By Type' }
              ].map(view => (
                <Button
                  key={view.key}
                  variant={selectedView === view.key ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedView(view.key as any)}
                >
                  {view.label}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {selectedView === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Credential Distribution */}
              <div>
                <h4 className="font-medium text-charcoal-ink mb-4">Credential Distribution by Type</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Industry Usage Trends */}
              <div>
                <h4 className="font-medium text-charcoal-ink mb-4">Usage by Industry</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={industryData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="usage" fill="#3B82F6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {selectedView === 'issuers' && (
            <div>
              <h4 className="font-medium text-charcoal-ink mb-4">Top Credential Issuers</h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organization</TableHead>
                    <TableHead>Credentials Issued</TableHead>
                    <TableHead>Market Share</TableHead>
                    <TableHead>Performance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.topIssuers.map((issuer, index) => (
                    <TableRow key={issuer.organizationId}>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 rounded-full bg-electric-steel" />
                          <span className="font-medium">{issuer.organizationName}</span>
                        </div>
                      </TableCell>
                      <TableCell>{issuer.credentialsIssued.toLocaleString()}</TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Progress value={issuer.percentage} className="flex-1" />
                          <span className="text-sm font-medium">{issuer.percentage}%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={index < 3 ? 'default' : 'secondary'}>
                          #{index + 1}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {selectedView === 'verifiers' && (
            <div>
              <h4 className="font-medium text-charcoal-ink mb-4">Top Credential Verifiers</h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organization</TableHead>
                    <TableHead>Verifications Performed</TableHead>
                    <TableHead>Market Share</TableHead>
                    <TableHead>Performance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.topVerifiers.map((verifier, index) => (
                    <TableRow key={verifier.organizationId}>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 rounded-full bg-verified-green" />
                          <span className="font-medium">{verifier.organizationName}</span>
                        </div>
                      </TableCell>
                      <TableCell>{verifier.verificationsPerformed.toLocaleString()}</TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Progress value={verifier.percentage} className="flex-1" />
                          <span className="text-sm font-medium">{verifier.percentage}%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={index < 3 ? 'default' : 'secondary'}>
                          #{index + 1}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {selectedView === 'types' && (
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-charcoal-ink mb-4">Credential Activity by Type</h4>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={utilizationData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="issued" fill="#3B82F6" name="Issued" />
                    <Bar dataKey="verified" fill="#10B981" name="Verified" />
                    <Bar dataKey="requested" fill="#FBBF24" name="Requested" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div>
                <h4 className="font-medium text-charcoal-ink mb-4">Credential Type Performance</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Credential Type</TableHead>
                      <TableHead>Issued</TableHead>
                      <TableHead>Verified</TableHead>
                      <TableHead>Requested</TableHead>
                      <TableHead>Trend</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.credentialsByType.map((credential) => (
                      <TableRow key={credential.type}>
                        <TableCell className="font-medium">{credential.type}</TableCell>
                        <TableCell>{credential.issued.toLocaleString()}</TableCell>
                        <TableCell>{credential.verified.toLocaleString()}</TableCell>
                        <TableCell>{credential.requested.toLocaleString()}</TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            {getTrendIcon(credential.trend)}
                            <span className={`text-sm ${getTrendColor(credential.trend)}`}>
                              {credential.trend}
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}